function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import map from 'lodash/map';
import first from 'lodash/first';
import findIndex from 'lodash/findIndex';
import cn from 'classnames';
import { connect } from 'react-redux';
import { getSecondsFromMidnight, SECONDS_IN_DAY } from 'helpers/datetime';
import ActivityTooltip from 'components/RelatedData/ActivityTooltip';
import HbA1CTooltip from 'components/RelatedData/HbA1CTooltip';
import MealTooltip from 'components/RelatedData/MealTooltip';
import PillTooltip from 'components/RelatedData/PillTooltip';
import InjectionTooltip from 'components/RelatedData/InjectionTooltip';
import NoteTooltip from 'components/RelatedData/NoteTooltip';
import * as actions from 'modules/PatientResults/actions';
import * as shapes from '../../shapes';
import Stack from './Stack';
import DayTimeline from './DayTimeline';
import Reading from './Reading';
import ReadingTooltip from './ReadingTooltip';
import StackedReadingsTooltip from './StackedReadingsTooltip';
import RelatedData from './RelatedData';
import { getStackedData } from './utils';
import styles from './DailyReadingsDistribution.pcss';
var _React_PureComponent;
class DailyReadingsDistribution extends (_React_PureComponent = React.PureComponent) {
    static getDerivedStateFromProps(props, state) {
        const { readings, relatedData } = props;
        const { width } = state;
        if (width) {
            const { separateData: separateReadings, stackedData: stackedReadings } = getStackedData(readings, width);
            const { separateData: separateRelatedData, stackedData: stackedRelatedData } = getStackedData(relatedData, width);
            return {
                width,
                stackedReadings,
                separateReadings,
                separateRelatedData,
                stackedRelatedData
            };
        }
        return null;
    }
    componentDidMount() {
        const { width } = this;
        this.setState({
            width
        });
    }
    onShowTooltip(type, data, style) {
        this.setState({
            tooltip: {
                type,
                data,
                style
            }
        });
    }
    onHideTooltip() {
        this.setState({
            tooltip: null
        });
    }
    get width() {
        const boundingRect = this.containerElement.current.getBoundingClientRect();
        return boundingRect.width;
    }
    renderSeparateReading(reading) {
        const { timestamp, isActive } = reading;
        // eslint-disable-next-line no-mixed-operators
        const left = `${getSecondsFromMidnight(reading.timestamp) / SECONDS_IN_DAY * 100}%`;
        const style = {
            left
        };
        return(// eslint-disable-next-line jsx-a11y/click-events-have-key-events
        /*#__PURE__*/ _jsx("div", {
            className: cn(styles.dailyMeasurementsDistribution__reading, {
                [styles['dailyMeasurementsDistribution__reading--isActive']]: isActive
            }),
            style: style,
            onClick: ()=>{
                this.props.setSelectedReading(reading);
            },
            onMouseEnter: ()=>this.onShowTooltip('reading', reading, style),
            onMouseLeave: ()=>this.onHideTooltip(),
            children: /*#__PURE__*/ _jsx(Reading, {
                reading: reading
            })
        }, `reading-${timestamp}`));
    }
    renderSeparateReadings() {
        const { separateReadings } = this.state;
        return map(separateReadings, (reading)=>this.renderSeparateReading(reading));
    }
    renderStackedReadings() {
        const { stackedReadings } = this.state;
        return map(stackedReadings, (stack)=>{
            const firstReading = first(stack);
            const { timestamp } = firstReading;
            // eslint-disable-next-line no-mixed-operators
            const left = `${getSecondsFromMidnight(timestamp) / SECONDS_IN_DAY * 100}%`;
            const style = {
                left
            };
            const activeElementIndex = findIndex(stack, (reading)=>reading.isActive);
            return /*#__PURE__*/ _jsx("div", {
                className: styles.dailyMeasurementsDistribution__readingStack,
                style: style,
                children: /*#__PURE__*/ _jsx(Stack, {
                    data: stack,
                    activeElementIndex: activeElementIndex,
                    tooltipType: "measurement"
                })
            }, `stacked-reading-${timestamp}`);
        });
    }
    renderStackedRelatedData() {
        const { stackedRelatedData } = this.state;
        return map(stackedRelatedData, (stack)=>{
            const firstReading = first(stack);
            const { timestamp } = firstReading;
            // eslint-disable-next-line no-mixed-operators
            const left = `${getSecondsFromMidnight(timestamp) / SECONDS_IN_DAY * 100}%`;
            const style = {
                left
            };
            const activeElementIndex = findIndex(stack, (reading)=>reading.isActive);
            return /*#__PURE__*/ _jsx("div", {
                className: styles.dailyMeasurementsDistribution__relatedDataStack,
                style: style,
                children: /*#__PURE__*/ _jsx(Stack, {
                    data: stack,
                    activeElementIndex: activeElementIndex,
                    tooltipType: "related"
                })
            }, `stacked-reading-${timestamp}`);
        });
    }
    renderRelatedData() {
        const { separateRelatedData } = this.state;
        return map(separateRelatedData, (data)=>{
            const { timestamp, carbs, resourceType } = data;
            // eslint-disable-next-line no-mixed-operators
            const left = `${getSecondsFromMidnight(timestamp) / SECONDS_IN_DAY * 100}%`;
            const style = {
                left
            };
            return /*#__PURE__*/ _jsx("div", {
                className: styles.dailyMeasurementsDistribution__relatedData,
                type: "related-data",
                style: style,
                onMouseEnter: ()=>this.onShowTooltip(resourceType ? 'timeSeriesResource' : 'relatedData', data, style),
                onMouseLeave: ()=>this.onHideTooltip(),
                children: /*#__PURE__*/ _jsx(RelatedData, {
                    data: data
                })
            }, `${data.type}-${timestamp}-${carbs}`);
        });
    }
    renderTooltip() {
        const { tooltip } = this.state;
        if (!tooltip) {
            return null;
        }
        const { type, data, style } = tooltip;
        if (type === 'reading') {
            return /*#__PURE__*/ _jsx("div", {
                className: styles.dailyMeasurementsDistribution__readingTooltip,
                style: style,
                children: /*#__PURE__*/ _jsx(ReadingTooltip, {
                    reading: data,
                    conversion: this.props.conversion
                })
            });
        }
        if (type === 'stackedReadings') {
            return /*#__PURE__*/ _jsx("div", {
                className: styles.dailyMeasurementsDistribution__readingStackTooltip,
                style: style,
                children: /*#__PURE__*/ _jsx(StackedReadingsTooltip, {
                    readings: data,
                    conversion: this.props.conversion
                })
            });
        }
        const dataType = data.resourceType || data.type; // TSR | RD
        let relatedDataTooltip;
        switch(dataType){
            case 'Meal':
            case 'food':
                relatedDataTooltip = /*#__PURE__*/ _jsx(MealTooltip, {
                    meal: data
                });
                break;
            case 'Activity':
            case 'activity':
                relatedDataTooltip = /*#__PURE__*/ _jsx(ActivityTooltip, {
                    activity: data
                });
                break;
            case 'HbA1C':
                relatedDataTooltip = /*#__PURE__*/ _jsx(HbA1CTooltip, {
                    HbA1CResource: data
                });
                break;
            case 'Medication':
                if (data.bolusFlag || data.basalFlag) {
                    relatedDataTooltip = /*#__PURE__*/ _jsx(InjectionTooltip, {
                        injection: data
                    });
                } else {
                    relatedDataTooltip = /*#__PURE__*/ _jsx(PillTooltip, {
                        pill: data
                    });
                }
                break;
            case 'pill':
                relatedDataTooltip = /*#__PURE__*/ _jsx(PillTooltip, {
                    pill: data
                });
                break;
            case 'injection':
                relatedDataTooltip = /*#__PURE__*/ _jsx(InjectionTooltip, {
                    injection: data
                });
                break;
            case 'Note':
                relatedDataTooltip = /*#__PURE__*/ _jsx(NoteTooltip, {
                    noteResource: data
                });
                break;
            default:
                relatedDataTooltip = null;
        }
        return /*#__PURE__*/ _jsx("div", {
            className: styles.dailyMeasurementsDistribution__relatedDataTooltip,
            style: style,
            children: relatedDataTooltip
        });
    }
    render() {
        return /*#__PURE__*/ _jsxs("div", {
            className: styles.dailyMeasurementsDistribution,
            ref: this.containerElement,
            children: [
                /*#__PURE__*/ _jsx(DayTimeline, {}),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.dailyMeasurementsDistribution__markersLine,
                    children: [
                        this.renderSeparateReadings(),
                        this.renderStackedReadings(),
                        this.renderRelatedData(),
                        this.renderStackedRelatedData(),
                        this.renderTooltip()
                    ]
                })
            ]
        });
    }
    constructor(){
        super();
        this.containerElement = /*#__PURE__*/ React.createRef();
        this.state = {
            tooltip: null,
            width: 0,
            stackedReadings: [],
            separateReadings: [],
            stackedRelatedData: [],
            separateRelatedData: []
        };
    }
}
_define_property(DailyReadingsDistribution, "propTypes", {
    // Explicit props
    // eslint-disable-next-line react/no-unused-prop-types
    readings: PropTypes.arrayOf(shapes.reading),
    relatedData: PropTypes.array.isRequired,
    conversion: PropTypes.object.isRequired,
    // Explicit actions
    setSelectedReading: PropTypes.func
});
const mapDispatchToProps = (dispatch)=>({
        setSelectedReading: (reading)=>dispatch(actions.setSelectedReading(reading))
    });
const ConnectedDailyReadingsDistribution = connect(null, mapDispatchToProps)(DailyReadingsDistribution);
export default withStyles(styles)(ConnectedDailyReadingsDistribution);
